import React, {useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams, Redirect} from 'react-router-dom';
import {UserProfileContext} from '../../../components/UserProfileContext.js';
import {isValidNIN} from '../../../utils/string';
import isValidDateOfBirth, {isDateOfBirthWithinAcceptableRange} from '../../../utils/date';
import Form from '../Form';
import {isValidUKMobile} from '../utils/phoneNumber.js';
import getForms from './utils/forms';
import getFormsSpecials from './utils/formsSpecials';
import formDefinition from './utils/formDefinition';
import formDefinitionSpecials from './utils/formDefinitionSpecials';
import PropTypes from 'prop-types';
import {COMMON, OPTIONAL_FIELDS} from '../../../utils/applicationConst';

const getFormDefinitionForProduct = (userProfile, productId) => {
  if (productId?.toLowerCase() === COMMON.SPECIALS.toLowerCase() || productId?.toLowerCase() === COMMON.PCSO.toLowerCase())
    return formDefinitionSpecials(userProfile);

  return formDefinition(userProfile);
};

const getFormsForProduct = (productId) => {
  if (productId?.toLowerCase() === COMMON.SPECIALS.toLowerCase() || productId?.toLowerCase() === COMMON.PCSO.toLowerCase())
    return getFormsSpecials;

  return getForms;
};

const RecruitmentForm = (props) => {
  const {userProfile} = useContext(UserProfileContext);
  const {product, force} = props;

  const productId = product.uniqueID;
  const forceName = force.name;
  const definition = getFormDefinitionForProduct(userProfile, productId);
  // Set selected force and product in form data
  definition['role-force'].data = {role: product.id, force: force.id};

  const {slug} = useParams();
  const {t} = useTranslation();

  const [form, setForm] = useState({
    activeStep: props.activeStep || 0,
    ...definition,
  });
  const activeStep = form.activeStep;

  const isNotOptionalField = (field) => !OPTIONAL_FIELDS.includes(field);

  const isFieldValueEmpty = (fieldValue) => !fieldValue || 0 === fieldValue.length;

  const isValid = (step) => {
    const fieldError = {};
    const errorFields = Object.keys(form[step].data).filter(isNotOptionalField).filter(
      field => {
        if (field === 'firstLanguage') return form[slug].data['firstLanguageEnglish'] === 'no' && isFieldValueEmpty(form[slug].data[field]);
        if (field === 'previousPolicingExperience') return form[slug].data['anyPreviousPolicingExperience'] === 'yes' && isFieldValueEmpty(form[slug].data[field]);
        if (field === 'gender') return form[slug].data['genderMatch'] === 'no' && 0 === form[slug].data[field].length;
        if (field === 'dateOfBirth') {
          if (!isValidDateOfBirth(form[slug].data.dateOfBirth)) return true;
          if (!isDateOfBirthWithinAcceptableRange(form[slug].data.dateOfBirth, 16, 80)) {
            fieldError.dateOfBirth = t('common.dateOfBirthAgeError');
            return true;
          }
          return false;
        }
        if (field === 'phoneNumber') {
          const phoneNumber = form[slug].data[field];
          return phoneNumber ? !isValidUKMobile(phoneNumber) : false;
        }
        if (field === 'disabilityCategory') {
          return form[slug].data['disability'] === 'yes' && isFieldValueEmpty(form[slug].data[field]);
        }
        if (field === 'otherNationalities') {
          return form[slug].data['nationalities']?.includes('other') && !form[slug].data['otherNationalities']?.filter(Boolean)?.length;
        }
        if (field === 'nationalInsuranceNumber') {
          return !isValidNIN(form[slug].data[field]);
        } else {
          return isFieldValueEmpty(form[step].data[field]);
        }
      }
    );
    setForm({
      ...form,
      [step]: {...form[step], errors: [...errorFields], fieldError},
    });

    return errorFields.length === 0;
  };

  const isNotLinkingToFirstPage = () => {
    return slug !== 'personal-details' && activeStep === 0;
  };

  if (isNotLinkingToFirstPage()) {
    return <Redirect to='/recruitment' />;
  }

  return (
    <React.Fragment>
      <Form title="College of Policing registration form"
        isValid={isValid}
        getForms={getFormsForProduct(productId)}
        activeStep={activeStep}
        type="recruitment"
        slug={slug}
        form={form}
        setForm={setForm}
        formDefinition={formDefinition}
        forceName={forceName}
        submitBtnLabel={'Apply'}
        productId={productId}
      />
    </React.Fragment>
  );
};
RecruitmentForm.propTypes = {
  product: PropTypes.object.isRequired,
  force: PropTypes.object.isRequired,
  activeStep: PropTypes.number
};
export default RecruitmentForm;
